<template>
  <Dialog
    :visible="isVisible"
    :pt="{
      root: { style: { background: 'white', borderRadius: '2rem', width: '500px' } },
      mask: {
        style: 'backdrop-filter: blur(1px); background-color: #00000014; pointer-events: auto; overflow-y: scroll;',
      },
    }"
    @hide="resetModal"
  >
    <template #container>
      <Button
        :class="$style.closeButton"
        icon="pi pi-times"
        class="p-button-rounded p-button-text"
        @click="resetModal"
      />

      <div :class="$style.wrapper">
        <div :class="$style.title">Create offer</div>
        <div class="p-fluid">
          <div class="p-fluid">
            <div class="p-field" :class="$style.input">
              <label for="offerName" :class="$style.label">Offer name</label>
              <InputText
                id="offerName"
                v-model="offer.name"
                placeholder="Identifier for the offer"
                style="width: 100%"
              />
              <div :class="$style.notice">Use only alphanumeric characters, dollar signs and spaces</div>
              <div v-if="!!formErrors.name" :class="$style.error">{{ formErrors.name }}</div>
            </div>
            <div class="p-field" :class="$style.input">
              <label :class="$style.label" for="duration">Duration</label>
              <div :class="$style.calendarSection">
                <div>Start</div>
                <InputGroup icon-position="left">
                  <InputGroupAddon>
                    <i class="pi pi-calendar" />
                  </InputGroupAddon>
                  <DatePicker
                    id="durationStart"
                    v-model="offer.durationStart"
                    date-format="dd/mm/yy"
                    placeholder="Start date"
                    :min-date="new Date()"
                  />
                </InputGroup>

                <div>End</div>
                <InputGroup icon-position="left">
                  <InputGroupAddon>
                    <i class="pi pi-calendar" />
                  </InputGroupAddon>
                  <DatePicker
                    id="durationEnd"
                    v-model="offer.durationEnd"
                    date-format="dd/mm/yy"
                    placeholder="End date"
                    :min-date="new Date()"
                  />
                </InputGroup>
              </div>
              <div v-if="!!formErrors.durationStart" :class="$style.error">{{ formErrors.durationStart }}</div>
              <div v-if="!!formErrors.durationEnd" :class="$style.error">{{ formErrors.durationEnd }}</div>
            </div>
          </div>
          <div :class="$style.buttonContainer">
            <Button
              :class="$style.actionButton"
              label="Save"
              :disabled="loading"
              :loading="loading"
              @click="attemptSaveOffer"
            />
            <Button :class="$style.actionButton" label="Cancel" class="p-button-text" @click="resetModal" />
          </div>
        </div>
      </div>
    </template>
  </Dialog>
</template>

<script setup>
import formatDateTime from "@/utils/formatDateTime";
import Button from "primevue/button";
import DatePicker from "primevue/datepicker";
import Dialog from "primevue/dialog";
import InputGroup from "primevue/inputgroup";
import InputGroupAddon from "primevue/inputgroupaddon";
import InputText from "primevue/inputtext";
import { computed, reactive, ref, watch } from "vue";

import * as yup from "yup";

const props = defineProps({
  isVisible: Boolean,
  loading: Boolean,
  campaignId: String,
});

const emit = defineEmits(["saveOffer", "close"]);

const offer = reactive({
  name: "",
  triggers: [],
  durationStart: null,
  durationEnd: null,
});

const resetOffer = () => {
  offer.name = "";
  offer.durationStart = null;
  offer.durationEnd = null;
};

const formErrors = ref({});

const resetModal = () => {
  resetOffer();
  emit("close");
  formErrors.value = {};
};

const schema = computed(() =>
  yup.object({
    name: yup.string().required("Offer name is required"),
    durationStart: yup
      .date()
      .required("Start date is required")
      .min(new Date(new Date().setHours(0, 0, 0, 0)), "Start date must be after or equal to today's date"),
    durationEnd: yup
      .date()
      .required("End date is required")
      .min(
        new Date(
          offer.durationStart ? new Date(offer.durationStart).setHours(0, 0, 0, 0) : new Date().setHours(0, 0, 0, 0)
        ),
        "End date must be after or equal to start date"
      ),
  })
);

const validateForm = async () => {
  try {
    formErrors.value = {};

    await schema.value.validate(offer, { abortEarly: false });
  } catch (err) {
    if (err instanceof yup.ValidationError) {
      err.inner.forEach((error) => {
        if (error.path) {
          formErrors.value[error.path] = error.message;
        }
      });
    }
  }
};

watch(
  offer,
  async () => {
    if (Object.keys(formErrors.value).length > 0) {
      await validateForm();
    }
  },
  { deep: true }
);

const attemptSaveOffer = async () => {
  await validateForm();
  if (Object.keys(formErrors.value).length === 0) {
    const offerDetails = {
      name: offer.name.trim(),
      startDate: formatDateTime(offer.durationStart),
      endDate: formatDateTime(offer.durationEnd),
      status: "DRAFT",
      campaignId: props.campaignId === "noCampaign" ? null : props.campaignId,
    };
    emit("saveOffer", offerDetails);
  }
};
</script>

<style module>
.triggerInput {
  font-size: 1rem;
  background: #ffffff;
  padding: 0.1rem 0.75rem;
  border: 1px solid #d1d5db;
  transition: background-color 0.2s, color 0.2s, border-color 0.2s, box-shadow 0.2s;
  border-radius: 6px;
  min-height: 40px;
  display: flex;
  align-items: center;
}

.triggerInputFocus {
  outline: 0 none;
  outline-offset: 0;
  box-shadow: 0 0 0 0.2rem #bfdbfe;
  border-color: #3b82f6;
}

.triggerSelector {
  width: calc(100% - 4rem); /* 3rem being the padding left + right of the modal */
  position: absolute;
  z-index: 1;
  box-shadow: 0 4px 8px rgba(0, 0, 0, 0.1);
  border-radius: 6px;
  margin-top: 0;
}

.triggerValue {
  margin-right: 0.3rem;
}

.wrapper {
  background-color: white;
  padding: 2rem;
  border-radius: 2rem;
}

.title {
  font-weight: 700;
  font-size: 16px;
  margin-bottom: 2rem;
}

.label {
  font-size: 12px;
  font-weight: 400;
  margin-bottom: 0.5rem;
  display: block;
}

.input {
  margin-bottom: 1.5rem;
}

.notice {
  font-size: 12px;
  font-weight: 400;
  color: var(--vd-color-text-light-2);
  margin-top: 0.5rem;
  display: inline-block;
}

.calendarSection {
  display: flex;
  gap: 1rem;
  align-items: center;
}

.checkboxWrapper {
  display: flex;
  align-items: center;
  gap: 1rem;
  font-size: 12px;
}

.error {
  font-size: 12px;
  color: #f87171;
  margin-top: 0.5rem;
}

.buttonContainer {
  display: flex;
  justify-content: flex-end;
  gap: 10px;
}

.actionButton {
  width: 100px;
}

.closeButton {
  position: absolute;
  right: 1rem;
  top: 1rem;
}

.timerSection {
  display: flex;
  gap: 1rem;
  align-items: flex-start;
  margin: 8px 8px;
}

.timerPickerSection {
  margin: 8px 8px;
  max-width: 200px;
}
.timerPickerDisabled {
  background-color: #f4f5f7;
  color: #d1d5db;
  cursor: not-allowed;
}
.cursor-pointer {
  cursor: pointer;
}
</style>
